import { Container } from "@dinahmoe/dmafjs-visual";
import { coreEngine, nodes, store } from "@dinahmoe/dmafjs-core";

/**
 * NetworkContainer Component
 *
 * ARCHITECTURE:
 * - Component (this file): Creates nodes, manages state and data
 * - Environment: Handles positioning, animations, and DOM manipulation
 *
 * FLOW:
 * 1. Component creates category AND case nodes at init (all visible at depth)
 * 2. Environment positions cases with random scale and half distance
 * 3. Click category: cases animate to full size and full distance
 * 4. Click again: cases animate back to depth
 */
class NetworkContainer extends Container {
  namespace = "network:container";

  handlers = {
    scrollValue: this.handleScrollValue
  };

  handleScrollValue(node) {
    if (!node._currentCategory) return;

    const height = node['size.height'];
    if (height <= 0) return;

    const threshold = height / 2;

    if (Math.abs(node.scrollValue - node.centerScrollY) > threshold) {
      this.collapseCategory(node);
    }
  }

  init(node, event) {
    super.init(node, event);

    node._categoryNodes = [];
    node._categories = [];
    node._currentCategory = null;
    node._currentCategoryData = null;
    node._casesPerCategory = new Map(); // categoryUid -> [{ node, caseData, index }]
    node._selectedCaseNode = null;

    const casesData = store.get("assets.data.cases_export");

    if (casesData) {
      node._categories = casesData.categories;
      node._casesData = casesData;
      this.createCategories(node);
      this.createAllCases(node);
    }
  }

  createCategories(node) {
    const template = store.get("template.visual.network_category");
    if (!template) return;

    node._categories.forEach((category, index) => {
      const categoryNode = coreEngine.create({
        ...template,
        parentUid: node.uid,
        'position.x': 0,
        'position.y': 0,
        categoryName: category.name,
        containerUid: node.uid
      }, { key: "create_category" });

      node._categoryNodes.push({
        node: categoryNode,
        category,
        index
      });
    });
  }

  createAllCases(node) {
    const template = store.get("template.visual.network_case");
    if (!template) return;

    node._categoryNodes.forEach(categoryData => {
      const cases = node._casesData.case_stories.filter(
        c => c.category_id === categoryData.category.id
      );

      const caseNodes = [];
      cases.forEach((caseData, index) => {
        const caseNode = coreEngine.create({
          ...template,
          parentUid: categoryData.node.uid,
          'position.x': 0,
          'position.y': 0,
          caseName: caseData.headline_clickbait || caseData.headline,
          containerUid: node.uid
        }, { key: "create_case" });

        caseNodes.push({ node: caseNode, caseData, index });
      });

      node._casesPerCategory.set(categoryData.node.uid, caseNodes);
    });
  }

  handleCategoryClick(node, categoryData) {
    if (node._currentCategory === categoryData.node.uid) {
      if (node._selectedCaseNode) {
        // Case is open — close it, keep category expanded
        nodes.get("container_case_story")?.tell({ type: "close" });
        node._selectedCaseNode.selected = false;
        node._selectedCaseNode = null;
        if (node._currentCategoryData) {
          node._currentCategoryData.node.caseSelected = false;
        }
      } else {
        this.collapseCategory(node);
      }
    } else if (node._currentCategory) {
      this.switchCategory(node, categoryData);
    } else {
      this.expandCategory(node, categoryData);
    }
  }

  expandCategory(node, categoryData) {
    categoryData.node.selected = true;
    node._currentCategory = categoryData.node.uid;
    node._currentCategoryData = categoryData;

    const caseStoryNode = nodes.get("container_case_story");
    if (caseStoryNode) {
      caseStoryNode.tell({ type: "category_expanded", categoryId: categoryData.category.id });
    }

    this.dispatch("showcase:category_clicked");
    this.scrollToCenter(node);
  }

  switchCategory(node, categoryData) {
    const oldCategoryData = node._categoryNodes.find(c => c.node.uid === node._currentCategory);
    if (oldCategoryData) {
      oldCategoryData.node.selected = false;
    }

    if (node._selectedCaseNode) {
      node._selectedCaseNode.selected = false;
      node._selectedCaseNode = null;
    }
    if (node._currentCategoryData) {
      node._currentCategoryData.node.caseSelected = false;
    }

    this.expandCategory(node, categoryData);
  }

  collapseCategory(node) {
    if (node._selectedCaseNode) {
      nodes.get("container_case_story")?.tell({ type: "close" });
      node._selectedCaseNode.selected = false;
      node._selectedCaseNode = null;
    }

    const categoryData = node._categoryNodes.find(c => c.node.uid === node._currentCategory);
    if (categoryData) {
      categoryData.node.selected = false;
      categoryData.node.caseSelected = false;
    }
    node._currentCategory = null;
    node._currentCategoryData = null;
  }

  state(node, config, event) {
    const parts = event.name.split(":");
    const action = parts[parts.length - 1];

    if (action === "closed") {
      if (node._selectedCaseNode) {
        node._selectedCaseNode.selected = false;
        node._selectedCaseNode = null;
      }
      if (node._currentCategoryData) {
        node._currentCategoryData.node.caseSelected = false;
      }
    }

    if (action === "case_changed") {
      if (event.data?.sourceUid && event.data.sourceUid !== node.uid) return;

      const categoryId = event.data?.categoryId;
      if (categoryId) {
        const targetCategoryData = node._categoryNodes.find(c => c.category.id === categoryId);
        if (targetCategoryData && node._currentCategory !== targetCategoryData.node.uid) {
          if (node._currentCategory) {
            this.switchCategory(node, targetCategoryData);
          } else {
            this.expandCategory(node, targetCategoryData);
          }
        }
      }

      if (node._selectedCaseNode) {
        node._selectedCaseNode.selected = false;
        node._selectedCaseNode = null;
      }

      const newCaseId = event.data?.caseId;
      if (newCaseId) {
        for (const [catUid, cases] of node._casesPerCategory) {
          const caseInfo = cases.find(c => c.caseData.id === newCaseId);
          if (caseInfo) {
            caseInfo.node.selected = true;
            node._selectedCaseNode = caseInfo.node;
            if (node._currentCategoryData) {
              node._currentCategoryData.node.caseSelected = true;
            }
            this.scrollToCenter(node);
            break;
          }
        }
      }
    }
  }

  scrollToCenter(node) {
    if (node.smootherUid) {
      const smoother = nodes.get(node.smootherUid);
      if (smoother) {
        smoother["value.target"] = node.centerScrollY;
      }
    }
  }

  receiveMessage(node, message) {
    if (message.type === "deep_link_case") {
      const categoryData = node._categoryNodes.find(
        c => c.category.id === message.categoryId
      );
      if (categoryData) {
        this.handleCategoryClick(node, categoryData);
        const cases = node._casesPerCategory.get(categoryData.node.uid);
        const caseInfo = cases?.find(c => c.caseData.id === message.caseId);
        if (caseInfo) {
          nodes.get("container_case_story")?.tell({
            type: "open_case",
            caseId: caseInfo.caseData.id,
            sourceUid: node.uid
          });
          caseInfo.node.selected = true;
          node._selectedCaseNode = caseInfo.node;
          categoryData.node.caseSelected = true;
        }
      }
      return;
    }

    if (message.type === "category_clicked") {
      const categoryData = node._categoryNodes.find(c => c.node.uid === message.categoryUid);
      if (categoryData) {
        this.handleCategoryClick(node, categoryData);
      }
      return;
    }

    if (message.type === "case_clicked") {
      const caseUid = message.caseUid;

      if (node._lastClickUid === caseUid) return;
      node._lastClickUid = caseUid;
      queueMicrotask(() => { node._lastClickUid = null; });

      let caseInfo = null;
      for (const [catUid, cases] of node._casesPerCategory) {
        caseInfo = cases.find(c => c.node.uid === caseUid);
        if (caseInfo) break;
      }

      if (!caseInfo) {
        console.error("[network-container] caseInfo not found for", caseUid);
        return;
      }

      if (node._selectedCaseNode === caseInfo.node) {
        nodes.get("container_case_story")?.tell({ type: "close" });
      } else {
        // Find which category this case belongs to
        let parentCategoryData = null;
        for (const [catUid, cases] of node._casesPerCategory) {
          if (cases.some(c => c.node.uid === caseUid)) {
            parentCategoryData = node._categoryNodes.find(c => c.node.uid === catUid);
            break;
          }
        }

        // Expand the parent category if not already expanded
        if (parentCategoryData && node._currentCategory !== parentCategoryData.node.uid) {
          if (node._currentCategory) {
            this.switchCategory(node, parentCategoryData);
          } else {
            this.expandCategory(node, parentCategoryData);
          }
        }

        nodes.get("container_case_story")?.tell({
          type: "open_case",
          caseId: caseInfo.caseData.id,
          sourceUid: node.uid
        });

        if (node._selectedCaseNode) {
          node._selectedCaseNode.selected = false;
        }
        caseInfo.node.selected = true;
        node._selectedCaseNode = caseInfo.node;

        if (node._currentCategoryData) {
          node._currentCategoryData.node.caseSelected = true;
        }

        this.scrollToCenter(node);
      }
      return;
    }

    super.receiveMessage(node, message);
  }
}

const networkContainer = new NetworkContainer();

export { NetworkContainer, networkContainer };
